import React, { createContext, Dispatch, FunctionComponent, ReactNode, useContext, useEffect, useReducer } from 'react';

import * as FullStory from '@fullstory/browser';
import amplitude from 'amplitude-js';

import { ActionId } from 'admin/actions/Actions';
import { PreferenceKey } from 'admin/users/Users';
import { Profile, useProfile } from 'auth/Auth';
import { useConfig } from 'core/util/Config';
import 'core/util/Track.scss';

export enum Category {
    Action = 'ACTION',
    PageView = 'PAGEVIEW',
    Survey = 'SURVEY',
    FilterChange = 'FILTERCHANGE',
}

export interface Event {
    app: string;
    name: string;
    properties: {
        client?: string;
        [propName: string]: any;
    };
}

export interface TrackContainer {
    profile: Profile;
    amplitudeEnabled: boolean;
    fullstoryEnabled: boolean;
}

export enum TrackActionType {
    AmplitudeInit,
    FullStoryInit,
    Track,
}

export interface TrackAction {
    type: TrackActionType;
    event?: Event;
    amplitudeApiKey?: string;
    fullstoryOrg?: string;
}

interface TrackProviderProps {
    children: ReactNode;
}

interface UserProperties {
    client: string;
    agencyId?: number;
    tenantId?: number;
    tenantType?: number;
    accountType?: number;
    isImpersonated?: boolean;
    impersonatedByUserId?: number;
    impersonatedByAgencyId?: number;
    userRoleIds?: string;
}

const TrackStateContext = createContext<TrackContainer | undefined>(undefined);
const TrackDispatchContext = createContext<Dispatch<TrackAction> | undefined>(undefined);

const trackReducer = (trackContainer: TrackContainer, action: TrackAction): TrackContainer => {
    switch (action.type) {
        case TrackActionType.AmplitudeInit:
            // amplitude.getInstance().init(action.amplitudeApiKey!);

            return {
                profile: trackContainer.profile,
                amplitudeEnabled: false,
                fullstoryEnabled: trackContainer.fullstoryEnabled,
            };

        case TrackActionType.FullStoryInit:
            FullStory.init({ orgId: action.fullstoryOrg! });
            FullStory.setUserVars({
                uid: trackContainer.profile.user?.id,
                displayName: trackContainer.profile.user?.name,
                email: trackContainer.profile.user?.email,
                agencyId: trackContainer.profile.user?.agencyID,
                tenantId: trackContainer.profile.user?.tenantID,
            });

            return {
                profile: trackContainer.profile,
                amplitudeEnabled: trackContainer.amplitudeEnabled,
                fullstoryEnabled: true,
            };

        case TrackActionType.Track:
            if (trackContainer.amplitudeEnabled) {
                const { profile } = trackContainer;

                const userProperties: UserProperties = {
                    client: 'r4e-ui2',
                };

                userProperties.userRoleIds = profile.user.roleIDs.join(', ');

                if (profile.isAgencyUser) {
                    userProperties.agencyId = profile.agency!.id;
                } else {
                    userProperties.tenantId = profile.tenant!.id;
                    userProperties.tenantType = profile.tenant!.tenantType;
                    userProperties.accountType = profile.tenant!.accountType;
                    userProperties.isImpersonated = profile.isImpersonated;

                    if (profile.impersonatedBy) {
                        userProperties.impersonatedByUserId = profile.impersonatedBy.userId;
                        userProperties.impersonatedByAgencyId = profile.impersonatedBy.agencyId;
                    }
                }

                // User properties
                amplitude.getInstance().setUserId(String(profile.user.id));
                amplitude.getInstance().setUserProperties(userProperties);

                action.event!.properties.client = 'r4e-ui2'; // eslint-disable-line no-param-reassign

                // Track
                // console.log('Track', action.event!.name, action.event!.properties);
                amplitude.getInstance().logEvent(action.event!.name, action.event!.properties);
            }

            return { ...trackContainer };

        default:
            throw new TypeError(`unrecognized action type ${action.type}`);
    }
};

export const TrackProvider: FunctionComponent<TrackProviderProps> = ({ children }) => {
    const [{ profile }] = useProfile();

    const [state, dispatch] = useReducer(trackReducer, {
        profile: profile!,
        amplitudeEnabled: false,
        fullstoryEnabled: false,
    });

    return (
        <TrackStateContext.Provider value={state}>
            <TrackDispatchContext.Provider value={dispatch}>{children}</TrackDispatchContext.Provider>
        </TrackStateContext.Provider>
    );
};

const useTrackState = (): TrackContainer => {
    const context = useContext(TrackStateContext);

    if (context === undefined) {
        throw new Error('useTrackState must be used within a TrackProvider');
    }

    return context;
};

const useTrackDispatch = (): Dispatch<TrackAction> => {
    const context = useContext(TrackDispatchContext);

    if (context === undefined) {
        throw new Error('useTrackDispatch must be used within a TrackProvider');
    }

    return context;
};

const getBrowserName = (userAgent: any): string => {
    // The order matters here, and this may report false positives for unlisted browsers.
    if (userAgent.includes('Firefox')) {
        // "Mozilla/5.0 (X11; Linux i686; rv:104.0) Gecko/20100101 Firefox/104.0"
        return 'Mozilla Firefox';
    }
    if (userAgent.includes('SamsungBrowser')) {
        // "Mozilla/5.0 (Linux; Android 9; SAMSUNG SM-G955F Build/PPR1.180610.011) AppleWebKit/537.36 (KHTML, like Gecko) SamsungBrowser/9.4 Chrome/67.0.3396.87 Mobile Safari/537.36"
        return 'Samsung Internet';
    }
    if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
        // "Mozilla/5.0 (Macintosh; Intel Mac OS X 12_5_1) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/104.0.0.0 Safari/537.36 OPR/90.0.4480.54"
        return 'Opera';
    }
    if (userAgent.includes('Trident')) {
        // "Mozilla/4.0 (compatible; MSIE 7.0; Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; .NET CLR 2.0.50727; .NET CLR 3.0.30729; .NET CLR 3.5.30729)"
        return 'Microsoft Internet Explorer';
    }
    if (userAgent.includes('Edge')) {
        // "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299"
        return 'Microsoft Edge (Legacy)';
    }
    if (userAgent.includes('Edg')) {
        // "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/104.0.0.0 Safari/537.36 Edg/104.0.1293.70"
        return 'Microsoft Edge (Chromium)';
    }
    if (userAgent.includes('Chrome')) {
        // "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/104.0.0.0 Safari/537.36"
        return 'Google Chrome or Chromium';
    }
    if (userAgent.includes('Safari')) {
        // "Mozilla/5.0 (iPhone; CPU iPhone OS 15_6_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/15.6 Mobile/15E148 Safari/604.1"
        return 'Apple Safari';
    }
    return 'unknown';
};

export const useTrack = (): [TrackContainer, Dispatch<TrackAction>] => {
    const [trackContainer, dispatch] = [useTrackState(), useTrackDispatch()];
    const [{ config }] = useConfig();
    const [{ profile }] = useProfile();

    const amplitudeConfig = {
        enabled: false,
        apiKey: config!.secrets['amplitude.apiKey'] && (config!.secrets['amplitude.apiKey'].value as string),
    };

    const walkmeConfig = {
        enabled: config!.secrets['walkme.enabled'] && config!.secrets['walkme.enabled'].value === true,
        link: config!.secrets['walkme.link'] && (config!.secrets['walkme.link'].value as string),
    };

    const fullstoryConfig = {
        enabled: config!.secrets['fullstory.enabled'] && config!.secrets['fullstory.enabled'].value === true,
        org: config!.secrets['fullstory.org'] && (config!.secrets['fullstory.org'].value as string),
    };

    const levelaccessConfig = {
        enabled: config!.secrets['levelaccess.enabled'] && config!.secrets['levelaccess.enabled'].value === true,
        key: config!.secrets['levelaccess.key'] && (config!.secrets['levelaccess.key'].value as string),
    };

    const pendoConfig = {
        enabled: config!.secrets['pendo.enabled'] && config!.secrets['pendo.enabled'].value === true,
        apiKey: config!.secrets['pendo.apiKey'] && (config!.secrets['pendo.apiKey'].value as string),
    };

    const salesforcechatConfig = {
        enabled: config!.secrets['salesforcechat.enabled'] && config!.secrets['salesforcechat.enabled'].value === true,
        key1: config!.secrets['salesforcechat.key1'] && (config!.secrets['salesforcechat.key1'].value as string),
        key2: config!.secrets['salesforcechat.key2'] && (config!.secrets['salesforcechat.key2'].value as string),
        key3: config!.secrets['salesforcechat.key3'] && (config!.secrets['salesforcechat.key3'].value as string),
        scrt2URL:
            config!.secrets['salesforcechat.scrt2URL'] && (config!.secrets['salesforcechat.scrt2URL'].value as string),
        scripturl:
            config!.secrets['salesforcechat.scripturl'] &&
            (config!.secrets['salesforcechat.scripturl'].value as string),
    };

    const repIqConfig = {
        url: config!.secrets['repIq.url']
            ? (config!.secrets['repIq.url'].value as string)
            : 'https://insights.reputation.com/app/bundle-repIQWidget.js',
    };

    const userAgent = window.navigator.userAgent.toLowerCase();
    const isMobileWebView = userAgent.includes('wv') || /(iphone)(?!.*safari)/.test(userAgent);

    useEffect(() => {
        if (!trackContainer.amplitudeEnabled) {
            if (amplitudeConfig.enabled) {
                if (
                    profile?.user &&
                    profile?.user.preferences &&
                    profile?.user.preferences[PreferenceKey.PrivacySettingsOptIn] !== 'false'
                ) {
                    dispatch({
                        type: TrackActionType.AmplitudeInit,
                        amplitudeApiKey: amplitudeConfig.apiKey,
                    });
                }
            }
        }

        if (walkmeConfig.enabled) {
            setTimeout(() => {
                if (!document.getElementById('walkme')) {
                    ((): void => {
                        const script = document.createElement('script');

                        script.id = 'walkme';
                        script.type = 'text/javascript';
                        script.async = true;
                        script.src = walkmeConfig.link;

                        document.body.appendChild(script);

                        (window as any)._walkmeConfig = { smartLoad: true }; // eslint-disable-line no-underscore-dangle
                    })();

                    // Initialize data for walkme
                    if (trackContainer.profile.user) {
                        (window as any).userid = trackContainer.profile.user.id;
                        (window as any).agencyid = trackContainer.profile.user.agencyID;
                        (window as any).tenantid = trackContainer.profile.user.tenantID;
                        (window as any).roleid = trackContainer.profile.user.roleID;
                        (window as any).roleids = trackContainer.profile.user.roleIDs;
                        (window as any).locale = trackContainer.profile.user.locale;
                        (window as any).actions = trackContainer.profile.actions;
                    }

                    if (trackContainer.profile.tenant) {
                        (window as any).tenanttype = trackContainer.profile.tenant.tenantType;
                        (window as any).accounttype = trackContainer.profile.tenant.accountType;
                    }

                    if (trackContainer.profile.actions) {
                        (window as any).showEducation = trackContainer.profile.actions[ActionId.PlatformEducation.View];
                    }
                }
            }, 2000);
        }

        if (!trackContainer.fullstoryEnabled) {
            if (fullstoryConfig.enabled) {
                if (
                    profile?.user &&
                    profile?.user.preferences &&
                    profile?.user.preferences[PreferenceKey.PrivacySettingsOptIn] !== 'false'
                ) {
                    dispatch({
                        type: TrackActionType.FullStoryInit,
                        fullstoryOrg: fullstoryConfig.org,
                    });
                }
            }
        }

        function initEmbeddedMessaging() {
            if (
                !trackContainer!.profile!.isAgencyUser &&
                !trackContainer!.profile!.isImpersonated &&
                (window as any).profile.featureFlags &&
                !isMobileWebView &&
                !(window as any).profile.featureFlags.hide_web_chat
            ) {
                try {
                    (window as any).embeddedservice_bootstrap.settings.language = 'en_US'; // For example, enter 'en' or 'en-US'
                    // Add hidden prechat fields

                    window.addEventListener('onEmbeddedMessagingReady', () => {
                        console.log('Received the onEmbeddedMessagingReady event…');
                        (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
                            Locale: trackContainer.profile.user.locale,
                            RDC_Account_Id: '',
                            RDC_Agency_Id: String(trackContainer.profile.user.agencyID),
                            RDC_Email: trackContainer.profile.user.email,
                            RDC_Locale: trackContainer.profile.user.locale,
                            RDC_Name: trackContainer.profile.user.name,
                            RDC_Phone:
                                trackContainer.profile.user && trackContainer.profile.user.phone
                                    ? trackContainer.profile.user.phone
                                    : '',
                            RDC_Platform: 'Reputation',
                            RDC_Received_Date: new Date().toISOString(),
                            RDC_Tenant_Id: String(trackContainer.profile.user.tenantID),
                            RDC_Tenant_Id__c: String(trackContainer.profile.user.tenantID),
                            RDC_Agency_Id__c: String(trackContainer.profile.user.agencyID),
                        });
                    });

                    // Set the position of the chat button
                    (window as any).embeddedservice_bootstrap.settings.chatButtonPosition = {
                        position: 'bottom-left',
                        horizontal: 'left',
                        vertical: 'bottom',
                    };

                    (window as any).embeddedservice_bootstrap.settings.chatWindowPosition = {
                        position: 'bottom-left',
                        horizontal: 'left',
                        vertical: 'bottom',
                    };

                    (window as any).embeddedservice_bootstrap.init(
                        salesforcechatConfig.key1,
                        salesforcechatConfig.key2,
                        salesforcechatConfig.key3,
                        {
                            scrt2URL: salesforcechatConfig.scrt2URL,
                        },
                    );
                } catch (err) {
                    console.error('Error loading Embedded Messaging: ', err);
                }
            }
        }

        const script = document.createElement('script');

        script.id = 'salesforce_eistein';
        script.type = 'text/javascript';
        script.async = true;
        script.src = salesforcechatConfig.scripturl;
        script.onload = initEmbeddedMessaging;
        document.body.appendChild(script);

        if (levelaccessConfig.enabled) {
            setTimeout(() => {
                if (!document.getElementById('levelaccess')) {
                    ((): void => {
                        const script = document.createElement('script');

                        script.id = 'levelaccess';
                        script.type = 'text/javascript';
                        script.async = true;
                        script.src = `https://cdn.levelaccess.net/accessjs/${levelaccessConfig.key}/access.js`;

                        document.body.appendChild(script);
                    })();
                }
            }, 2000);
        }

        if (pendoConfig.enabled) {
            setTimeout(() => {
                if (!document.getElementById('pendo')) {
                    ((): void => {
                        // adding this check to prevent the browser error: Uncaught TypeError: Cannot read properties of undefined (reading 'initialize')
                        if ((window as any).pendo && typeof (window as any).pendo.initialize === 'function') {
                            (window as any).pendo.initialize({
                                visitor: {
                                    id: trackContainer.profile.user.id,
                                    roleId: trackContainer.profile.user.roleID,
                                    roleIds: trackContainer.profile.user.roleIDs,
                                    locale: trackContainer.profile.user.locale,
                                    userName: trackContainer.profile.user.name,
                                    email: trackContainer.profile.user.email,
                                    impersonatedByAgencyId: trackContainer.profile.impersonatedBy?.agencyId,
                                    impersonatedByUserIdId: trackContainer.profile.impersonatedBy?.userId,
                                    isImpersonated: trackContainer.profile.isImpersonated ? 'true' : 'false',
                                    os: getBrowserName(navigator.userAgent),
                                    userCreatedDate: trackContainer.profile.user?.createdDate,
                                },

                                account: {
                                    id: trackContainer.profile.user?.tenantID,
                                    agencyId: trackContainer.profile.user?.agencyID,
                                    isAgencyUser: trackContainer.profile.isAgencyUser,
                                    accountType: trackContainer.profile.tenant?.accountType,
                                    tenantType: trackContainer.profile.tenant?.tenantType,
                                    tenantName: trackContainer.profile.tenant?.name,
                                    industry: trackContainer.profile.tenant?.industry,
                                    companyType: trackContainer.profile.tenant?.companyType,
                                    createdDate: trackContainer.profile.tenant?.createdDate,
                                },
                            });
                        }
                    })();
                }
            }, 2000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        trackContainer.profile,
        dispatch,
        amplitudeConfig,
        walkmeConfig,
        fullstoryConfig,
        levelaccessConfig,
        pendoConfig,
    ]);

    useEffect(() => {
        async function authenticateWithNuvi() {
            const repIqBtn = document.querySelector('.rep-iq-container img');
            const mount = document.body;
            let el = document.getElementsByTagName('rep-iq-widget') as any;

            if (!el[0]) {
                el = document.createElement('rep-iq-widget') as any;
                // eslint-disable-next-line
                mount.appendChild(el);

                (window as any).runRepIQ = function runRepIQ(page: string) {
                    el.setDataSources([page]);
                    const key = `r4e-filters-${trackContainer.profile.user.id}-${trackContainer.profile?.user.locale}`;
                    const initKey = `r4e-filters-${trackContainer.profile.user.id}-${trackContainer.profile?.user.locale}-initial`;

                    let filter = {};

                    if (localStorage.getItem(key)) {
                        filter = JSON.parse(localStorage.getItem(key)!);
                    } else {
                        filter = JSON.parse(localStorage.getItem(initKey)!);
                    }

                    el.setFilters(filter);
                    try {
                        console.log(page);
                        el.show();
                    } catch (ex) {
                        console.log(ex);
                    }
                };

                console.log({
                    tenantId: trackContainer.profile.user.tenantID,
                    userEmail: trackContainer.profile.user.email,
                    userId: trackContainer.profile.user.id,
                });

                const isAuthenticated = await (el as any).authenticate({
                    tenantId: trackContainer.profile.user.tenantID,
                    userEmail: trackContainer.profile.user.email,
                    userId: trackContainer.profile.user.id,
                });

                // Set Pendo agent ID for conversation tracking (reviews feed context)
                (el as any).setPendoAgentId('qQyKCye5zA24FDcInr69iBU2yxY');

                if (isAuthenticated && repIqBtn) {
                    repIqBtn.classList.remove('hide');
                }
            }
        }

        const el = document.getElementsByTagName('rep-iq-widget') as any;

        if (!el[0]) {
            const repIqScript = document.createElement('script');

            repIqScript.id = 'repiqscript';
            repIqScript.type = 'text/javascript';
            repIqScript.async = true;
            repIqScript.src = repIqConfig.url;
            repIqScript.onload = authenticateWithNuvi;
            document.body.appendChild(repIqScript);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return [trackContainer, dispatch];
};
